import posthog from 'posthog-js';
import type { PostHog } from 'posthog-js';
import { watch } from 'vue';
import type { Router } from 'vue-router';
import { useConsentState } from '../composables/consent/useConsentState';

interface PosthogPluginConfig {
  posthogKey: string;
  directHost: string;
  proxyHost?: string;
  router: Router;
}

export function createPosthogPlugin(config: PosthogPluginConfig) {
  const { posthogKey, directHost, proxyHost, router } = config;

  if (!posthogKey || !directHost) {
    return { posthog: () => null as PostHog | null };
  }

  const { hasAnalyticsConsent } = useConsentState();
  const analyticsGranted = hasAnalyticsConsent.value;

  const posthogClient = posthog.init(posthogKey, {
    api_host: directHost,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    persistence: analyticsGranted ? 'localStorage+cookie' : 'memory',
    opt_out_capturing_by_default: !analyticsGranted,
    loaded: (ph) => {
      if (import.meta.env.MODE === 'development') {
        ph.debug();
      }
    },
  });

  if (proxyHost) {
    fetch(`${directHost}/decide`, { method: 'GET', mode: 'cors' })
      .then((res) => {
        if (!res.ok) {
          posthog.set_config({ api_host: proxyHost });
        }
      })
      .catch(() => {
        posthog.set_config({ api_host: proxyHost });
      });
  }

  watch(hasAnalyticsConsent, (granted) => {
    if (granted) {
      posthog.opt_in_capturing();
      posthog.set_config({ persistence: 'localStorage+cookie' });
    } else {
      posthog.opt_out_capturing();
      posthog.set_config({ persistence: 'memory' });
    }
  });

  router.afterEach((to) => {
    if (hasAnalyticsConsent.value) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path: to.path,
        name: to.name as string,
      });
    }
  });

  return { posthog: () => posthogClient as PostHog | null };
}
